import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { CheckCircle2 } from "lucide-react";
import { CommitteeMember, TermsOfReference } from "@/types/governance";
import { useMemo, useState } from "react";

interface TorApprovalPanelProps {
  tor: TermsOfReference;
  members: CommitteeMember[];
  currentUserId: string;
  onApprove: (comment?: string) => void;
}

export function TorApprovalPanel({ tor, members, currentUserId, onApprove }: TorApprovalPanelProps) {
  const [comment, setComment] = useState("");
  const approverCount = tor.approverIds.length;
  const approvedCount = useMemo(
    () => new Set(tor.approvalLog.filter((entry) => entry.action === "approved").map((entry) => entry.userId)).size,
    [tor.approvalLog],
  );
  const alreadyApproved = tor.approvalLog.some(
    (entry) => entry.userId === currentUserId && entry.action === "approved",
  );
  const memberById = new Map(members.map((item) => [item.id, item]));

  return (
    <Card className="shadow-card">
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <span>Approval Log</span>
          <Badge variant="secondary">
            {approvedCount}/{approverCount} approved
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          {tor.approvalLog.map((entry) => (
            <div key={entry.id} className="rounded-md border p-3 text-sm">
              <div className="flex items-center justify-between">
                <p className="font-medium">{memberById.get(entry.userId)?.name || "Unknown"}</p>
                <Badge variant="outline">{entry.action}</Badge>
              </div>
              {entry.comment ? <p className="mt-1 text-muted-foreground">{entry.comment}</p> : null}
            </div>
          ))}
          {tor.approvalLog.length === 0 && (
            <p className="text-sm text-muted-foreground">No approvals recorded yet.</p>
          )}
        </div>

        <div className="space-y-2">
          <Textarea
            placeholder="Add optional feedback before approval..."
            value={comment}
            onChange={(event) => setComment(event.target.value)}
          />
          <Button
            onClick={() => {
              onApprove(comment || undefined);
              setComment("");
            }}
            disabled={alreadyApproved}
          >
            <CheckCircle2 className="mr-2 h-4 w-4" />
            {alreadyApproved ? "Already Approved" : "Mark Approved"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
